import { Component, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ColDef, ColGroupDef } from 'ag-grid-community';
import { formatNumber, NgStyle } from '@angular/common';
import { CalculationsService } from '../../../../../services/calculations/calculations.service';
import { Subscription } from 'rxjs';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { AgNoRowsOverlayComponent } from '../../../../ag-grid-components/ag-no-rows-overlay/ag-no-rows-overlay.component';
import { DxsrExcelStyling } from '../dxsr-excel-styling';
import { AuditAction } from '../../../../../models/audit-action';
import { LoggingService } from '../../../../../services/api/logging.service';
import { RootCauseTrendItem } from '../../../../../models/root-cause-trend-item';
import { AgGridFunctions } from '../../../../../helpers/ag-grid-functions';
import { AgLoadingOverlayComponent } from '../../../../ag-grid-components/ag-loading-overlay/ag-loading-overlay.component';
import { AgGridExportHelper, IQueryFilterRow } from '../../../../../helpers/ag-grid-export-helper';

@Component({
    selector: 'app-root-cause-trend-report',
    templateUrl: './root-cause-trend-report.component.html',
    styleUrls: ['./root-cause-trend-report.component.scss'],
    imports: [NgStyle, AgGridModule]
})
export class RootCauseTrendReportComponent implements OnInit, OnDestroy {

  data: RootCauseTrendItem[] = [];

  nonActionableRowName = 'Non-Actionable';
  completeRowName = 'Complete';

  columnDefs: (ColDef | ColGroupDef)[] = [];

  defaultColDef: ColDef = {
    menuTabs: [],
    minWidth: 100,
    maxWidth: 100,
    autoHeight: true
  };

  noRowsOverlayComponent: any = AgNoRowsOverlayComponent;
  noRowsOverlayComponentParams: any = {
    noRowsMessageFunc: () => 'No results returned',
  };

  loadingOverlayComponent: any = AgLoadingOverlayComponent;

  excelStyles = DxsrExcelStyling.excelStyles;

  gridContext: any;

  rowItems: any[] = [];
  gridReady = false;

  dxSwitch = false;

  currentStartYear = 0;
  currentEndYear = 0;

  currentRowHeight = 140;
  currentGridWidth = 282;

  subs: Subscription[] = [];

  @Output() ready: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() setExportEnable: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('grid') agGrid!: AgGridAngular;

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.setGridColumnsToFit();
  }

  agGridExportHelper: AgGridExportHelper = new AgGridExportHelper();

  constructor(
    private calculationsService: CalculationsService,
    private loggingService: LoggingService) {
    this.gridContext = { componentParent: this };
  }

  ngOnInit(): void {
    //TODO
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  onGridReady(event) {
    this.ready.emit(true);

    this.subs.push(this.calculationsService.rootCauseTrendCalculations.subscribe(result => {

      this.data = result || [];

      this.currentRowHeight = 96 + ((this.data.length + 3) * 29);
      this.currentGridWidth = 282 + (((this.currentEndYear - this.currentStartYear) + 1) * 200);

      this.setRowItems();
      this.agGrid?.api?.setGridOption('rowData', this.rowItems);
      this.agGrid?.api?.setGridOption('columnDefs', this.getColDef());
      this.hideGridsLoadingOverlay();
      this.setExportEnable.emit(true);

      setTimeout(() => {
        this.setGridColumnsToFit();
      }, 500);
    }));
  }

  updateServiceYearRange(startYear: number, endYear: number) {
    this.currentStartYear = startYear;
    this.currentEndYear = endYear;
    this.calculationsService.getRootCauseTrend(startYear, endYear);
    this.showGridsLoadingOverlay();
  }

  setRowItems() {
    this.rowItems = [];
    const nonActionTotals = [];
    const completeTotals = [];
    const grandTotals = [];

    for(let i = this.currentStartYear; i <= this.currentEndYear; i++) {
      let nonActionableTotal = 0;
      let completeTotal = 0;

      this.data.filter(d => d.ServiceYear == i).forEach(k => {
        nonActionableTotal += k.StatusID == 1 ? k.Count : 0;
        completeTotal += k.StatusID == 2 ? k.Count : 0;
      });

      this.data.filter(d => d.ServiceYear == i).forEach(k => {
        if(k.StatusID == 1) {
          k.Progress = nonActionableTotal > 0 ? k.Count / nonActionableTotal : 0;
        }

        if(k.StatusID == 2) {
          k.Progress = completeTotal > 0 ? k.Count / completeTotal : 0;
        }
      });

      const grandTotal = nonActionableTotal + completeTotal;

      nonActionTotals.push({ServiceYear: i, Count: nonActionableTotal, Progress: grandTotal > 0 ? nonActionableTotal / grandTotal : 0});
      completeTotals.push({ServiceYear: i, Count: completeTotal, Progress: grandTotal > 0 ? completeTotal / grandTotal : 0});
      // -1 sentinel suppresses Progress export/display for the Grand Total row.
      grandTotals.push({ServiceYear: i, Count: grandTotal, Progress: -1});
    }

    const grouped: {[key: number]: RootCauseTrendItem[]} = {};
    this.data.forEach(item => {
      const check = grouped[item.StatusID];
      if(!check) {
        grouped[item.StatusID] = [item];
        return;
      }

      grouped[item.StatusID].push(item);
    });

    Object.keys(grouped).forEach(k => {
      const statusId = Number(k);
      const items = grouped[statusId] || [];

      if(statusId == 1) {
        this.rowItems.push(this.addNewCategory(nonActionTotals, '', 'Non-Actionable'));
      }

      if(statusId == 2) {
        this.rowItems.push(this.addNewCategory(completeTotals, '', 'Complete'));
      }

      const rootCauseGrouped: {[key: string]: RootCauseTrendItem[]} = {};
      items.forEach(item => {
        const check = rootCauseGrouped[item.RootCause];
        if(!check) {
          rootCauseGrouped[item.RootCause] = [item];
          return;
        }

        rootCauseGrouped[item.RootCause].push(item);
      });

      const parent = statusId == 1 ? 'Non-Actionable' : 'Complete';
      Object.keys(rootCauseGrouped).forEach(rootCause => {
        this.rowItems.push(this.addNewCategory(rootCauseGrouped[rootCause], parent, rootCause));
      });
    });

    this.rowItems.push(this.addNewCategory(grandTotals, '', 'Grand Total'));
  }

  addNewCategory(dataItems: any[], parentName: string, displayName: string) {

    if(!dataItems || dataItems.length == 0) {
      return;
    }

    let rowItem = {};
    rowItem["DisplayName"] = displayName;
    rowItem["Parent"] = parentName;

    dataItems.forEach(i => {
      rowItem = this.addNewRowItemColumns(i, rowItem);
    });

    return rowItem;
  }

  addNewRowItemColumns(dataItem: RootCauseTrendItem, rowItem: any) {
    const serviceYear = dataItem.ServiceYear;

    rowItem[`${serviceYear}Count`] = dataItem.Count;
    rowItem[`${serviceYear}Progress`] = dataItem.Progress;

    return rowItem;
  }

  getColDef(): (ColDef | ColGroupDef)[] {

    const result: any = [
      { field: 'DisplayName', headerName: '',
        tooltip: (params) => params.data?.DisplayName,
        minWidth: 280,
        maxWidth: 600,
        // Keep this header in the bottom header row rather than spanning both, so the group row above it gets its blue filler cell
        suppressSpanHeaderHeight: true,
        cellClassRules: {
          'dxsr-display-name-1': (params) => {
            return params.data.DisplayName === 'Non-Actionable' 
            || params.data.DisplayName === 'Complete' 
            || params.data.DisplayName === 'Grand Total';
          },
          'dxsr-display-name-4': (params) => {
            return  params.data.Parent !== null && params.data.Parent !== "";
          },
        }
      },
    ];

    for(let i = this.currentStartYear; i <= this.currentEndYear; i++) {
      const serviceYear = i;
      const newYear = {
        headerName: `${serviceYear} Service Year`,
        children: [
          { field: `${serviceYear}Count`, headerName: 'Count', type: 'numericColumn', 
            valueFormatter: AgGridFunctions.getNumberValue,
            cellClassRules: {
              'dxsr-values-1': (params) => {
                return params.data.DisplayName === 'Non-Actionable' 
                || params.data.DisplayName === 'Complete' 
                || params.data.DisplayName === 'Grand Total';
              },
              'dxsr-values-4': (params) => {
                return params.data.Parent !== null && params.data.Parent !== "";
              }
            }
          },
          { field: `${serviceYear}Progress`, headerName: 'Progress', type: 'numericColumn',
            valueFormatter: this.getPercentValue,
            cellClassRules: {
              'dxsr-percent-1': (params) => {
                return params.data.DisplayName === 'Non-Actionable' 
                || params.data.DisplayName === 'Complete' 
                || params.data.DisplayName === 'Grand Total';
              },
              'dxsr-percent-4': (params) => {
                return params.data.Parent !== null && params.data.Parent !== "";
              }
            }
          },
        ]
      }

      result.push(newYear);
    }

    return result;
  }

  setGridColumnsToFit() {
    // Called from a timeout; the id check alone passes if the next report reuses the same element id.
    if(!document.getElementById('grid') || this.agGrid?.api?.isDestroyed()) {
      return;
    }

    this.agGrid?.api?.sizeColumnsToFit();
  }

  showGridsLoadingOverlay() {
    this.agGrid?.api?.setGridOption('loading', true);
  }

  hideGridsLoadingOverlay() {
    this.agGrid?.api?.setGridOption('loading', false);
  }

  onExport() {
    const filename =`RootCauseTrendYTDExport_${this.currentStartYear}-${this.currentEndYear}`;
    this.agGridExportHelper.onExport(
      filename,
      'Root Cause Trend YTD',
      this.getQueryFilterRows(),
      this.agGrid,
      [], // excluded columns
      undefined, // sheet name resolver
      undefined // append content resolver
    );
  }

  getPercentValue(item: any) {
    if(item.data.DisplayName == 'Grand Total') {
      return '';
    }

    if(!item.value) {
      return '0%';
    }
    const num = formatNumber(item.value * 100, 'en', '1.0-1') + "%";
    return num;
  }

  private getQueryFilterRows() {
    const filters: IQueryFilterRow[] = [];
    filters.push({ label: 'Start Service Year', value: this.currentStartYear.toString(), type: 'Number' });
    filters.push({ label: 'End Service Year', value: this.currentEndYear.toString(), type: 'Number' });
    return filters;
  }

}
